import React, { useState } from 'react';
import styled from 'styled-components';
import api from '../api';
import { theme } from '../theme';
import { formatClock, useCoworkAdmin } from '../hooks/useCoworkAdmin';
import CoworkSessionTab from '../components/cowork/CoworkSessionTab';
import CoworkTasksTab from '../components/cowork/CoworkTasksTab';
import CoworkCommunityTab from '../components/cowork/CoworkCommunityTab';
import CoworkOverlaysTab from '../components/cowork/CoworkOverlaysTab';
import CoworkHistoryTab from '../components/cowork/CoworkHistoryTab';
import CoworkSettingsTab from '../components/cowork/CoworkSettingsTab';
import {
  Button,
  ErrorText,
  Muted,
  Page,
  PageHeader,
  PageHeaderMain,
  Subtitle,
  TabBar,
  TabBtn,
  Title,
} from '../components/ui';

type Tab = 'session' | 'tasks' | 'community' | 'overlays' | 'history' | 'settings';

const TABS: { id: Tab; label: string }[] = [
  { id: 'session', label: 'Sesión' },
  { id: 'tasks', label: 'Mis tareas' },
  { id: 'community', label: 'Comunidad' },
  { id: 'overlays', label: 'Overlays' },
  { id: 'history', label: 'Historial' },
  { id: 'settings', label: 'Configuración' },
];

const LiveStrip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 20, 147, 0.28);
  background: rgba(255, 20, 147, 0.08);
  font-size: 0.86rem;
  color: #fff;
  font-variant-numeric: tabular-nums;

  small {
    color: ${theme.colors.textMuted};
    font-size: 0.74rem;
    text-transform: uppercase;
    letter-spacing: 0.6px;
  }
`;

const HeaderSide = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const StatePill = styled.span<{ $off?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  border: 1px solid ${({ $off }) => ($off ? 'rgba(255, 255, 255, 0.18)' : 'rgba(157, 255, 176, 0.35)')};
  background: ${({ $off }) => ($off ? 'rgba(255, 255, 255, 0.05)' : 'rgba(157, 255, 176, 0.1)')};
  color: ${({ $off }) => ($off ? theme.colors.textMuted : '#9dffb0')};

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $off }) => ($off ? theme.colors.textMuted : '#9dffb0')};
  }
`;

const CoworkPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('session');
  const [toggling, setToggling] = useState(false);
  const { state, remainingMs, loading, error, refresh, run } = useCoworkAdmin();

  const enabled = state?.enabled !== false;

  // Apagado, ningún comando de cowork responde en el chat. No anuncia nada al cambiar.
  const toggleCowork = async () => {
    setToggling(true);
    const ok = await run(api.put('/cowork/settings', { enabled: !enabled }));
    setToggling(false);
    if (ok) await refresh();
  };

  return (
    <Page>
      <PageHeader>
        <PageHeaderMain>
          <Title>Cowork</Title>
          <Subtitle>
            Centro de control de los streams de cowork: pomodoro, tareas propias, tareas del chat y
            overlays de OBS.
          </Subtitle>
        </PageHeaderMain>
        <HeaderSide>
          <StatePill $off={!enabled}>{enabled ? 'Cowork encendido' : 'Cowork apagado'}</StatePill>
          <Button
            $variant={enabled ? 'ghost' : undefined}
            $size="sm"
            disabled={toggling || !state}
            onClick={toggleCowork}
          >
            {toggling ? '…' : enabled ? 'Apagar cowork' : 'Encender cowork'}
          </Button>
          {state && (
            <LiveStrip>
              <small>
                {state.timer.status === 'idle'
                  ? 'Sin sesión'
                  : state.timer.status === 'finished'
                    ? 'Completada'
                    : `${state.timer.phaseLabel}${state.timer.status === 'paused' ? ' · pausa' : ''}`}
              </small>
              <strong>{formatClock(remainingMs)}</strong>
              {state.timer.cycle > 0 && <small>{state.timer.cycle}/{state.timer.totalCycles}</small>}
            </LiveStrip>
          )}
        </HeaderSide>
      </PageHeader>

      {!enabled && state && (
        <Muted style={{ marginBottom: '0.6rem' }}>
          Cowork está apagado: los comandos del chat no responden a nadie y el bot no avisa nada. El
          panel sigue funcionando normalmente.
        </Muted>
      )}

      <TabBar>
        {TABS.map((item) => (
          <TabBtn key={item.id} $active={tab === item.id} onClick={() => setTab(item.id)}>
            {item.label}
          </TabBtn>
        ))}
      </TabBar>

      {error && <ErrorText>{error}</ErrorText>}
      {loading && !state && <Muted>Cargando Cowork…</Muted>}

      {state && tab === 'session' && (
        <CoworkSessionTab state={state} remainingMs={remainingMs} run={run} refresh={refresh} />
      )}
      {state && tab === 'tasks' && <CoworkTasksTab state={state} run={run} refresh={refresh} />}
      {tab === 'community' && <CoworkCommunityTab refresh={refresh} />}
      {tab === 'overlays' && <CoworkOverlaysTab />}
      {tab === 'history' && <CoworkHistoryTab />}
      {tab === 'settings' && <CoworkSettingsTab onSaved={refresh} />}
    </Page>
  );
};

export default CoworkPage;
